import React, {useEffect, useState, useCallback} from 'react';
import {
  emptyCheckerState,
  inferType,
  withSystem,
  setType,
  Plugins,
  TypeImplementation,
  Type,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {once} from '@packages/utilities/memo';
import {renderMode as renderModeType} from './renderMode';
import {loading as loadingType} from './loading';
import {matchJson} from '@packages/utilities/json';
import {constantFalse} from '@packages/utilities/fp/utils';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

import {
  TRecord,
  EEnum,
  ELiteral,
  TTypeConstructor,
  Expression,
  TText,
} from '@packages/systems/wfdl-ast';

const createContextualExpressionDefault = (expr: Expression, type: Type) => {
  const [, contextualExpr] = inferType(expr)(emptyCheckerState);
  return setType(contextualExpr, withSystem({isBindable: false}, type));
};

const Separator = emotionStyled('div')({
  width: '100%',
  height: tokens.space[2],
});

const animationPlugin = Plugins.name('Animation');

const pathType = withSystem(
  {isLocalizable: true},
  TTypeConstructor(qualify(animationPlugin, typeName('LottiePath')))
);
const durationType = withSystem(
  {isLocalizable: true},
  TTypeConstructor(qualify(animationPlugin, typeName('AnimationDuration')))
);
const loopType = withSystem(
  {isLocalizable: true},
  TTypeConstructor(qualify(animationPlugin, typeName('AnimationLoop')))
);
const playInReverseType = withSystem(
  {isLocalizable: true},
  TTypeConstructor(qualify(animationPlugin, typeName('AnimationPlayInReverse')))
);
const placeholderSrcType = withSystem(
  {
    hiddenProperty: true,
    label: 'Placeholder URL',
  },
  TText
);

const lottieAliasType = TRecord({
  path: pathType,
  renderMode: renderModeType,
  duration: durationType,
  loop: loopType,
  playInReverse: playInReverseType,
  loading: loadingType,
  placeholderSrc: placeholderSrcType,
});
const durationDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: qualify(animationPlugin, typeName('AnimationDuration')),
      value: null,
    }),
    durationType
  )
);
const loopDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: qualify(animationPlugin, typeName('AnimationLoop')),
      value: false,
    }),
    loopType
  )
);
const playInReverseDefault = once(() =>
  createContextualExpressionDefault(
    ELiteral({
      name: qualify(animationPlugin, typeName('AnimationPlayInReverse')),
      value: false,
    }),
    playInReverseType
  )
);
const renderModeDefault = once(() =>
  createContextualExpressionDefault(EEnum('svg'), renderModeType)
);
const loadingDefault = once(() =>
  createContextualExpressionDefault(EEnum('eager'), loadingType)
);

const getFieldFromExpr = (expr: ContextualExpression, fieldName: string) =>
  ContextualExpressions.getIn(expr, [{in: 'Record', at: fieldName}]);

function useLottieState(expr: ContextualExpression) {
  const [originalExpr, setOriginalExpr] = useState(expr);
  const [path, setPath] = useState(getFieldFromExpr(expr, 'path'));
  const [renderMode, setRenderMode] = useState(
    getFieldFromExpr(expr, 'renderMode')
  );
  const [duration, setDuration] = useState(getFieldFromExpr(expr, 'duration'));
  const [loop, setLoop] = useState(getFieldFromExpr(expr, 'loop'));
  const [playInReverse, setPlayInReverse] = useState(
    getFieldFromExpr(expr, 'playInReverse')
  );
  const [loading, setLoading] = useState(getFieldFromExpr(expr, 'loading'));

  useEffect(() => {
    if (expr !== originalExpr) {
      setPath(getFieldFromExpr(expr, 'path'));
      setRenderMode(getFieldFromExpr(expr, 'renderMode'));
      setDuration(getFieldFromExpr(expr, 'duration'));
      setLoop(getFieldFromExpr(expr, 'loop'));
      setPlayInReverse(getFieldFromExpr(expr, 'playInReverse'));
      setLoading(getFieldFromExpr(expr, 'loading'));
      setOriginalExpr(expr);
    }
  }, [expr, originalExpr]);

  return {
    path,
    renderMode,
    duration,
    loop,
    playInReverse,
    loading,
  };
}

const matchPathLiteral = ContextualExpressions.matchExpression({
  Literal: ({value}) => value,
  _: () => null,
});

const matchSrcJson = matchJson({
  Null: constantFalse,
  Boolean: constantFalse,
  Number: constantFalse,
  String: constantFalse,
  Array: constantFalse,
  Object: (fields) => Boolean(fields.src),
});

const LottieRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const {path, renderMode, duration, loop, playInReverse, loading} =
    useLottieState(expr);
  const isPerformanceSettingsEnabled = useFeatureFlag(
    FEATURE_FLAGS.LOTTIE_SPLINE_PERFORMANCE
  );

  const eeDispatch = useCallback(
    // @ts-expect-error - TS7006 - Parameter 'property' implicitly has an 'any' type.
    (property) => {
      return extendExpressionEditorActionAtPath(dispatch)([
        {in: 'Record', at: property},
      ]);
    },
    [dispatch]
  );

  if (path === undefined) {
    return null;
  }
  const pathValue = matchPathLiteral(stripAnn(path));

  const hasSrc = matchSrcJson(pathValue);

  return (
    <>
      <ExpressionEditor dispatch={eeDispatch('path')} expression={path} />
      {hasSrc ? (
        <>
          <Separator />
          <ExpressionEditor
            dispatch={eeDispatch('duration')}
            expression={duration || durationDefault()}
          />
          <Separator />
          <ExpressionEditor
            dispatch={eeDispatch('loop')}
            expression={loop || loopDefault()}
          />
          <Separator />
          <ExpressionEditor
            dispatch={eeDispatch('playInReverse')}
            expression={playInReverse || playInReverseDefault()}
          />
          {isPerformanceSettingsEnabled ? (
            <>
              <Separator />
              <ExpressionEditor
                dispatch={eeDispatch('loading')}
                expression={loading || loadingDefault()}
              />
            </>
          ) : null}
          <Separator />
          <ExpressionEditor
            dispatch={eeDispatch('renderMode')}
            expression={renderMode || renderModeDefault()}
          />
        </>
      ) : null}
    </>
  );
};

export const lottieType = TypeImplementation.Alias({
  type: lottieAliasType,
  render: LottieRender,
});
